"use client";

import { SelectOffices } from "@cmp-src/form/OfficeSelectorInput/SelectOffices";
import { Button } from "@cmp-src/primitives/button";
import { Dialog, DialogContent } from "@cmp-src/primitives/Dialog";
import { IResource, WorkplaceRequestLocationValue } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import { Plus, X } from "lucide-react";
import React from "react";
import { FloorSelector } from "@/features/map/components/FloorSelector";
import { MapPointPickerLayer } from "@/features/map/components/MapView/MapPointPickerLayer";
import { MapView } from "@/features/map/components/MapView/MapView";
import { useMapViewSvgEdit } from "@/features/map/components/MapView/MapViewSvg/useMapViewSvgEdit";
import { StaticMapPinLayer } from "@/features/map/components/MapView/StaticMapPinLayer";
import { QuickDeskCreator } from "@/features/map/components/QuickDeskCreator";
import { MapProvider, useMap } from "@/features/map/MapProvider";
import { ReserveProvider } from "@/features/reserve/providers/ReserveProvider";

/** A point picked on the map, in SVG user-space coordinates, with its floor. */
export interface MapPointSelection {
  x: number;
  y: number;
  floorId: string;
  floorName?: string;
}

/** The location kinds the finder can author when `enableLocationModes` is on. */
export type LocationPickMode = "resource" | "floor" | "coordinates";

interface ResourceFinderModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the chosen resource and its floor display name (if resolvable).
   *  Not needed when `enableLocationModes` is on (use `onSelectLocation`). */
  onSelect?: (resource: IResource, floorName?: string) => void;
  /** Office to open on; defaults to the current office. */
  initialOfficeId?: string;
  /** Hide the office selector and keep the finder pinned to `initialOfficeId`. */
  lockOffice?: boolean;
  /**
   * Pick a point on the map (a dropped pin) instead of a resource. When true the
   * confirm reports the pinned coordinates via `onSelectPoint`.
   */
  pointMode?: boolean;
  /** Called with the pinned point when `pointMode` is on. */
  onSelectPoint?: (point: MapPointSelection) => void;
  /**
   * Show the Desk / Floor / Point mode toggle inside the modal and report the
   * chosen location (resource, floor or pinned point) via {@link onSelectLocation}.
   */
  enableLocationModes?: boolean;
  onSelectLocation?: (location: WorkplaceRequestLocationValue) => void;
  /** The mode the toggle opens on (e.g. the kind of the current value). */
  initialLocationMode?: LocationPickMode;
  /**
   * A pin (e.g. an already-pinned request location) to show read-only on the
   * map as a reference while picking — jumps the finder to its floor on open,
   * and renders the pin whenever that floor is the one displayed.
   */
  referencePoint?: MapPointSelection;
  /**
   * Floor to open on when there's no {@link referencePoint} to derive one
   * from (e.g. the existing location is a floor rather than a dropped pin).
   * Ignored when `referencePoint` is set — it already carries a floor.
   */
  initialFloorId?: string;
}

/**
 * A stripped-down version of the reservation map used purely to pick a location:
 * office + floor selectors, the interactive map, and a mode-aware confirm. With
 * `enableLocationModes` it also owns the Desk/Floor/Point toggle.
 */
export const ResourceFinderModal: React.FC<ResourceFinderModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialOfficeId,
  lockOffice = false,
  pointMode = false,
  onSelectPoint,
  enableLocationModes = false,
  onSelectLocation,
  initialLocationMode = "resource",
  referencePoint,
  initialFloorId
}) => {
  const {
    userContext: { currentOffice },
    setUserContext
  } = useAuthenticated();

  // Track the latest global office so the restore can tell whether the finder
  // actually changed it (a closure value would be stale at cleanup time).
  const currentOfficeIdRef = React.useRef<string | undefined>(
    currentOffice?.id
  );
  currentOfficeIdRef.current = currentOffice?.id;

  // The office selector switches the global office context; restore it when the
  // finder closes — or unmounts while still open (e.g. navigation) — so browsing
  // the finder doesn't change the user's current office. Running the restore in
  // the cleanup covers both the close and the unmount-while-open cases.
  React.useEffect(() => {
    if (!isOpen) return;
    const previous = currentOffice?.id ?? null;
    return () => {
      if (previous && previous !== currentOfficeIdRef.current) {
        setUserContext({ type: "updateCurrentOffice", payload: previous });
      }
    };
  }, [isOpen]);

  // Stable "now" for the finder's map. Refreshed each time it opens, but kept
  // stable across re-renders so a new inline ISO string doesn't keep retriggering
  // MapProvider's date-sync effect (which would re-initialize the map).
  const startDate = React.useMemo(() => new Date().toISOString(), [isOpen]);

  if (!isOpen) {
    return null;
  }

  const officeId = initialOfficeId ?? currentOffice?.id ?? undefined;

  return (
    <ReserveProvider isOnReservationModal={false} withModal={false}>
      <MapProvider
        isEmbedded
        officeId={officeId}
        initialFloorId={referencePoint?.floorId ?? initialFloorId}
        startDate={startDate}
        selectionMode
        disableUrlOfficeSync
      >
        <ResourceFinderOrchestrator
          onClose={onClose}
          onSelect={onSelect}
          lockOffice={lockOffice}
          pointMode={pointMode}
          onSelectPoint={onSelectPoint}
          enableLocationModes={enableLocationModes}
          onSelectLocation={onSelectLocation}
          initialLocationMode={initialLocationMode}
          referencePoint={referencePoint}
        />
      </MapProvider>
    </ReserveProvider>
  );
};

interface ResourceFinderContentProps {
  onClose: () => void;
  onSelect?: (resource: IResource, floorName?: string) => void;
  lockOffice: boolean;
  pointMode: boolean;
  onSelectPoint?: (point: MapPointSelection) => void;
  enableLocationModes: boolean;
  onSelectLocation?: (location: WorkplaceRequestLocationValue) => void;
  initialLocationMode: LocationPickMode;
  /** Admin-only "Create new desk" entry point — omitted (hiding the button)
   *  when the current user can't edit maps or no floor is selected yet. */
  onCreateDesk?: () => void;
  referencePoint?: MapPointSelection;
}

/**
 * Owns the create-desk detour: while active, {@link QuickDeskCreator} takes
 * over the whole screen instead of the picker dialog (it needs to sit above
 * the dialog's own overlay, so the dialog is unmounted rather than just
 * hidden behind it). Both are rendered inside the same map provider so the
 * picker's floor/office selection survives the detour, and once a desk is
 * created it's queued for selection as soon as the resources list picks it
 * up.
 */
const ResourceFinderOrchestrator: React.FC<ResourceFinderContentProps> = (
  props
) => {
  const { onClose, referencePoint } = props;
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { floor, currentOffice, allResources, setSelectedResource } = useMap();
  const [isCreatingDesk, setIsCreatingDesk] = React.useState(false);
  const [pendingResourceId, setPendingResourceId] = React.useState<
    string | null
  >(null);

  // Resolve the newly created desk into a real selection as soon as the
  // resources list (refetched by QuickDeskCreator's save) includes it.
  React.useEffect(() => {
    if (!pendingResourceId) return;
    const resource = allResources?.find((r) => r.id === pendingResourceId);
    if (!resource) return;
    setSelectedResource(resource);
    setPendingResourceId(null);
  }, [pendingResourceId, allResources, setSelectedResource]);

  const canCreateDesk =
    !!authenticatedUser?.permissions.settings &&
    !!floor?.map_data &&
    !!currentOffice;

  if (isCreatingDesk && floor && currentOffice) {
    return (
      <QuickDeskCreator
        floor={floor}
        officeId={currentOffice.id}
        referencePoint={
          referencePoint?.floorId === floor.id ? referencePoint : undefined
        }
        onDeskCreated={(resourceId) => {
          setIsCreatingDesk(false);
          setPendingResourceId(resourceId);
        }}
        onExit={() => setIsCreatingDesk(false)}
      />
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="h-[85vh] max-w-[92vw] gap-0 overflow-hidden border-none p-0"
        // biome-ignore lint/complexity/noUselessFragments: hide the default close button
        customCloseButton={<></>}
      >
        <div className="h-full w-full">
          <ResourceFinderContent
            {...props}
            onCreateDesk={
              canCreateDesk ? () => setIsCreatingDesk(true) : undefined
            }
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ResourceFinderContent: React.FC<ResourceFinderContentProps> = ({
  onClose,
  onSelect,
  lockOffice,
  pointMode,
  onSelectPoint,
  enableLocationModes,
  onSelectLocation,
  initialLocationMode,
  onCreateDesk,
  referencePoint
}) => {
  const {
    floor,
    floors,
    setFloor,
    currentOffice,
    changeOffice,
    selectedResource
  } = useMap();
  const editor = useMapViewSvgEdit();
  const mapWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [point, setPoint] = React.useState<{ x: number; y: number } | null>(
    null
  );
  const [mode, setMode] = React.useState<LocationPickMode>(initialLocationMode);

  // The point picker is on either via the standalone `pointMode` prop (seat/legacy
  // callers) or the "Point" location mode.
  const isPointMode = enableLocationModes ? mode === "coordinates" : pointMode;

  // A pin belongs to a floor — drop it when the floor changes.
  React.useEffect(() => {
    setPoint(null);
  }, [floor?.id]);

  const confirm = () => {
    if (!selectedResource) return;
    // A resource is selected on the currently displayed floor (switching floors
    // clears the selection), so the active floor is its floor. Matching
    // parent_id against floor ids fails for resources nested under an area.
    onSelect?.(selectedResource, floor?.name);
    onClose();
  };

  const confirmPoint = () => {
    if (!point || !floor?.id) return;
    onSelectPoint?.({
      x: point.x,
      y: point.y,
      floorId: floor.id,
      floorName: floor.name
    });
    onClose();
  };

  const confirmLocation = () => {
    if (mode === "resource") {
      if (!selectedResource) return;
      onSelectLocation?.({
        type: "resource",
        resource_id: selectedResource.id
      });
    } else if (mode === "floor") {
      if (!floor?.id) return;
      onSelectLocation?.({ type: "floor", floor_id: floor.id });
    } else {
      if (!point || !floor?.id) return;
      onSelectLocation?.({
        type: "coordinates",
        floor_id: floor.id,
        x: point.x,
        y: point.y
      });
    }
    onClose();
  };

  const locationStatus = () => {
    if (mode === "floor") {
      return floor
        ? `Floor: ${floor.name}`
        : "Choose a floor from the selector";
    }
    if (mode === "coordinates") {
      return point ? "Pinned location" : "Click on the map to drop a pin";
    }
    return selectedResource
      ? `Selected: ${selectedResource.label || selectedResource.name}`
      : "Select a resource on the map";
  };

  const locationDisabled =
    mode === "resource"
      ? !selectedResource
      : mode === "floor"
        ? !floor?.id
        : !point || !floor?.id;

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex flex-none items-center gap-3 border-neutral-200 border-b p-3">
        {enableLocationModes ? (
          <div className="inline-flex shrink-0 rounded-md border border-neutral-200 p-0.5">
            <ModeButton
              isActive={mode === "resource"}
              onClick={() => setMode("resource")}
            >
              Desk / room
            </ModeButton>
            <ModeButton
              isActive={mode === "floor"}
              onClick={() => setMode("floor")}
            >
              Floor
            </ModeButton>
            <ModeButton
              isActive={mode === "coordinates"}
              onClick={() => setMode("coordinates")}
            >
              Point
            </ModeButton>
          </div>
        ) : null}
        {lockOffice ? (
          <div className="flex h-10 items-center gap-1.5 rounded-md border border-neutral-200 bg-neutral-50 px-3 font-medium text-neutral-700 text-sm">
            {currentOffice?.name}
          </div>
        ) : (
          <div className="w-56 shrink-0">
            <SelectOffices
              multiselect={false}
              defaultValue={currentOffice?.id}
              onChange={(ids) => {
                if (ids[0]) changeOffice(ids[0]);
              }}
              placeholder="Select an office"
            />
          </div>
        )}
        {floor && floors && floors.length > 0 ? (
          <div className="w-56 shrink-0">
            <FloorSelector
              floorSelected={floor}
              changeFloor={setFloor}
              multipleFloors={floors}
            />
          </div>
        ) : null}
        {/* With the mode toggle hidden, `mode` may still be a carried-over
            "floor"/"coordinates" from `initialLocationMode` even though the
            footer below always resource-picks in that case — so treat a
            hidden toggle as resource mode too. */}
        {onCreateDesk && (mode === "resource" || !enableLocationModes) ? (
          <Button
            type="button"
            variant="outline"
            className="shrink-0 gap-1.5"
            onClick={onCreateDesk}
          >
            <Plus className="h-4 w-4" />
            Create new desk
          </Button>
        ) : null}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="ml-auto rounded-md p-1 text-neutral-400 hover:text-neutral-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div
        ref={mapWrapperRef}
        className="relative min-h-0 flex-1 overflow-hidden"
      >
        <MapView embedded editor={editor} isModal />
        {isPointMode ? (
          <MapPointPickerLayer
            mapWrapperRef={mapWrapperRef}
            point={point}
            onChange={setPoint}
          />
        ) : null}
        {referencePoint && referencePoint.floorId === floor?.id ? (
          <StaticMapPinLayer
            mapWrapperRef={mapWrapperRef}
            point={referencePoint}
          />
        ) : null}
      </div>

      <div className="flex flex-none items-center justify-between gap-3 border-neutral-200 border-t p-3">
        {enableLocationModes ? (
          <>
            <span className="truncate text-neutral-600 text-sm">
              {locationStatus()}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={locationDisabled} onClick={confirmLocation}>
                {mode === "floor"
                  ? "Select floor"
                  : mode === "coordinates"
                    ? "Select point"
                    : "Select resource"}
              </Button>
            </div>
          </>
        ) : isPointMode ? (
          <>
            <span className="truncate text-neutral-600 text-sm">
              {point ? (
                <span className="font-medium text-neutral-900">
                  Pinned location
                </span>
              ) : (
                "Click on the map to drop a pin"
              )}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={!point || !floor?.id} onClick={confirmPoint}>
                Select point
              </Button>
            </div>
          </>
        ) : (
          <>
            <span className="truncate text-neutral-600 text-sm">
              {selectedResource ? (
                <>
                  Selected:{" "}
                  <span className="font-medium text-neutral-900">
                    {selectedResource.label || selectedResource.name}
                  </span>
                </>
              ) : (
                "Select a resource on the map"
              )}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={!selectedResource} onClick={confirm}>
                Select resource
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ModeButton: React.FC<{
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ isActive, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "rounded px-2.5 py-1 font-medium text-sm transition-colors",
      isActive
        ? "bg-neutral-900 text-white"
        : "text-neutral-600 hover:text-neutral-900"
    )}
  >
    {children}
  </button>
);
